import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAuth } from '@/lib/auth-provider';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';
import {
  ACCEPT_CIRCLE_INVITE,
  ACCEPT_INVITATION,
  ACCEPT_GLOBAL_LINK_INVITATION,
  SIGN_IN_WEB,
} from '@/api/graphql/mutations';
import { CURRENT_USER } from '@/api/graphql/queries';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { LightModal, LightCloseButton, LightMark } from '../light-ui';
import { PhoneAuthFlow, type PhoneAuthSuccessPayload } from '@/components/PhoneAuthFlow';

interface RegistrationModalProps {
  open: boolean;
  onClose: () => void;
  onRegistered?: (user: any) => void;
  adminName?: string;
  circleName?: string;
  invitationEmail?: string;
  invitationToken?: string;
  invitationType?: 'CIRCLE' | 'GUEST_CONTRIBUTOR' | 'INTERVIEW' | 'GLOBAL_LINK';
  proxiCastName?: string;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({
  open,
  onClose,
  onRegistered,
  adminName = 'Admin',
  circleName = 'Circle',
  invitationEmail,
  invitationToken,
  invitationType = 'CIRCLE',
  proxiCastName,
}) => {
  const { loading, signInWithPhone } = useAuth();
  const [acceptCircleInvite] = useMutation(ACCEPT_CIRCLE_INVITE);
  const [acceptInvitation] = useMutation(ACCEPT_INVITATION);
  const [acceptGlobalLinkInvitation] = useMutation(ACCEPT_GLOBAL_LINK_INVITATION);
  const [signInWebMutation] = useMutation(SIGN_IN_WEB);

  // Phone-first signup is the default. "Use email instead" switches.
  // Name + channel + consent are collected later in ContributorOnboarding.
  const [signupMethod, setSignupMethod] = useState<'phone' | 'email'>('phone');

  const [form, setForm] = useState({
    email: invitationEmail || '',
    password: '',
    bio: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email signup gated on verification — onRegistered only fires after the
  // user clicks the link in the verification email.
  const [pendingVerify, setPendingVerify] = useState<{
    email: string;
    result: unknown;
  } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);
  const verifyPollingRef = useRef<number | null>(null);

  useEffect(() => {
    setForm(f => ({ ...f, email: invitationEmail || f.email }));
  }, [invitationEmail]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  useEffect(() => {
    if (!pendingVerify) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const u = auth.currentUser;
        if (!u) return;
        await u.reload();
        if (cancelled) return;
        if (u.emailVerified) {
          if (verifyPollingRef.current) window.clearInterval(verifyPollingRef.current);
          await u.getIdToken(true);
          const result = pendingVerify.result;
          setPendingVerify(null);
          onRegistered?.(result);
        }
      } catch {
        // network blip — keep polling
      }
    };
    verifyPollingRef.current = window.setInterval(poll, 3000);
    void poll();
    return () => {
      cancelled = true;
      if (verifyPollingRef.current) window.clearInterval(verifyPollingRef.current);
    };
  }, [pendingVerify, onRegistered]);

  const handleResendVerification = async () => {
    setResendError(null);
    const u = auth.currentUser;
    if (!u || !pendingVerify) return;
    try {
      await sendEmailVerification(u);
      setResendCooldown(30);
    } catch (err: any) {
      setResendError(err?.message || "Couldn't resend. Try again.");
    }
  };

  const handleCancelVerification = async () => {
    try {
      await auth.signOut();
    } catch {
      // ignore
    }
    setPendingVerify(null);
    setResendCooldown(0);
    setResendError(null);
  };

  const disabled = submitting || loading;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const acceptInvitationWithAuth = async (
    authToken: string,
    firstName: string,
    lastName: string,
  ) => {
    if (!invitationToken) throw new Error('No invitation token provided');
    const { data } = await acceptCircleInvite({
      variables: {
        token: invitationToken,
        authToken,
        firstName,
        lastName,
      },
    });
    if (data?.acceptCircleInvite?.success && data.acceptCircleInvite.user) return data.acceptCircleInvite.user;
    throw new Error(data?.acceptCircleInvite?.message || 'Failed to accept invitation');
  };

  const acceptGlobalLinkInvitationWithAuth = async () => {
    if (!invitationToken) throw new Error('No invitation token provided');
    const { data } = await acceptGlobalLinkInvitation({ variables: { input: { token: invitationToken } } });
    if (data?.acceptGlobalLinkInvitation?.success) {
      return {
        success: true,
        message: data.acceptGlobalLinkInvitation.message || 'Success',
        interview: data.acceptGlobalLinkInvitation.interview || null,
        category: data.acceptGlobalLinkInvitation.category || null,
      };
    }
    throw new Error(data?.acceptGlobalLinkInvitation?.message || 'Failed to accept global link invitation');
  };

  const acceptInvitationForExistingUser = async () => {
    if (!invitationToken) throw new Error('No invitation token provided');
    const { data } = await acceptInvitation({ variables: { token: invitationToken } });
    if (data?.acceptInvitation?.success) {
      return {
        success: true,
        invitation: data.acceptInvitation.invitation,
        message: data.acceptInvitation.message,
      };
    }
    throw new Error(data?.acceptInvitation?.message || 'Failed to accept invitation');
  };

  // Phone-OTP path: PhoneAuthFlow has just verified the user via Firebase.
  // signInWithPhone creates/binds the Proxi user. Name + consent come next
  // in ContributorOnboarding.
  const handlePhoneAuthSuccess = async ({ firebaseToken, phoneE164 }: PhoneAuthSuccessPayload) => {
    setError(null);
    setSubmitting(true);
    try {
      const phoneResult = await signInWithPhone(firebaseToken, phoneE164, {
        invitationToken,
      });
      if (phoneResult.needsLinking) {
        setError(
          `An account already exists for ${phoneResult.maskedContact || 'this contact'}. Please sign up with email instead.`,
        );
        setSignupMethod('email');
        return;
      }
      if (phoneResult.error) {
        setError(phoneResult.error);
        return;
      }
      if (invitationToken) {
        const result =
          invitationType === 'GLOBAL_LINK'
            ? await acceptGlobalLinkInvitationWithAuth()
            : await acceptInvitationForExistingUser();
        onRegistered?.(result);
      } else {
        onRegistered?.({ success: true });
      }
    } catch (err: any) {
      setError(err?.message || 'Phone sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      // Send verification email — onRegistered only fires after the user
      // clicks the link (polled by useEffect above).
      sendEmailVerification(userCredential.user).catch(() => {});
      const authToken = await userCredential.user.getIdToken();

      // Name + channel/phone/consent are captured later in onboarding.
      if (invitationType === 'GLOBAL_LINK') {
        await signInWebMutation({
          variables: {
            token: authToken,
            extraData: {
              email: form.email,
              userType: 'MEMBER',
            },
          },
          refetchQueries: [{ query: CURRENT_USER }],
          awaitRefetchQueries: true,
        });
      }

      let result;
      if (invitationType === 'GLOBAL_LINK') {
        result = await acceptGlobalLinkInvitationWithAuth();
      } else {
        result = await acceptInvitationWithAuth(authToken, '', '');
      }

      setPendingVerify({ email: form.email, result });
      setResendCooldown(30);
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        try {
          setError(null);
          const result = await acceptInvitationForExistingUser();
          onRegistered?.(result);
          return;
        } catch (inviteErr: any) {
          setError(inviteErr?.message || 'Failed to accept invitation for existing account.');
        }
      } else {
        setError(err?.message || 'Failed to register');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const headline =
    invitationType === 'GLOBAL_LINK'
      ? `${adminName} invited you to interview for ${proxiCastName || 'ProxiCast'}.`
      : `You've been invited to ${adminName}'s ${circleName}.`;

  return (
    <LightModal open={open} onClose={onClose} ariaLabel="Registration" width={560}>
      <LightCloseButton onClick={onClose} />
      <div className="lp-modal-body">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, marginBottom: 22 }}>
          <LightMark src={PROXI_BRAIN_LOGO} size={64} />
          <span className="lp-frost-chip">An invitation</span>
          <h2 className="lp-section-heading" style={{ fontSize: 'clamp(22px, 3.4vw, 30px)' }}>{headline}</h2>
          <p className="lp-prose" style={{ fontSize: 14, maxWidth: 420 }}>Get started with Proxi below.</p>
        </div>

        {pendingVerify ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, padding: '8px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: 999, background: 'rgba(236,72,153,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--lp-pink)" strokeWidth="2"><path d="M4 4h16v16H4z" /><path d="m4 4 8 8 8-8" /></svg>
            </div>
            <p style={{ fontWeight: 600, fontSize: 16, color: 'var(--lp-ink)' }}>Check your inbox</p>
            <p className="lp-prose" style={{ fontSize: 13, maxWidth: 360 }}>
              We sent a verification link to{' '}
              <span style={{ color: 'var(--lp-pink)' }}>{pendingVerify.email}</span>. Click it to continue.
            </p>
            {resendError && <p style={{ color: '#dc2626', fontSize: 12 }}>{resendError}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendCooldown > 0}
                className={resendCooldown > 0 ? '' : 'lp-btn-link'}
                style={{ fontSize: 12, opacity: resendCooldown > 0 ? 0.4 : 1, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer' }}
              >
                {resendCooldown > 0 ? `resend in ${resendCooldown}s` : 'resend email'}
              </button>
              <button
                type="button"
                onClick={handleCancelVerification}
                className="lp-btn-link"
                style={{ fontSize: 12 }}
              >
                wrong email?
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--lp-ink-mute)', fontSize: 12 }}>
              <span className="lp-spinner-sm" />
              waiting for verification…
            </div>
          </div>
        ) : signupMethod === 'phone' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PhoneAuthFlow onSuccess={handlePhoneAuthSuccess} />
            {error && <div className="lp-error">{error}</div>}
            <button
              type="button"
              onClick={() => { setSignupMethod('email'); setError(null); }}
              className="lp-link"
              style={{ alignSelf: 'center', fontSize: 12, background: 'none', border: 0, cursor: 'pointer' }}
            >
              Use email instead
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} autoComplete="off">
              <div>
                <label className="lp-field-label" htmlFor="reg-email">Email</label>
                <input
                  id="reg-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  disabled={disabled}
                  className="lp-input"
                />
              </div>

              <div>
                <label className="lp-field-label" htmlFor="reg-password">Password</label>
                <input
                  id="reg-password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  disabled={disabled}
                  className="lp-input"
                />
              </div>

              {invitationType === 'GLOBAL_LINK' && (
                <div>
                  <label className="lp-field-label" htmlFor="reg-bio">Short bio</label>
                  <textarea
                    id="reg-bio"
                    name="bio"
                    required
                    value={form.bio}
                    onChange={handleChange}
                    placeholder="Tell us a bit about yourself…"
                    rows={3}
                    disabled={disabled}
                    className="lp-textarea"
                  />
                </div>
              )}

              {error && <div className="lp-error">{error}</div>}

              <button type="submit" disabled={disabled} className="lp-btn lp-btn-primary" style={{ marginTop: 6 }} aria-busy={submitting}>
                {submitting ? (<><span className="lp-spinner-sm" /> Saving…</>) : 'Save & continue'}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button
                type="button"
                onClick={() => { setSignupMethod('phone'); setError(null); }}
                className="lp-link"
                style={{ fontSize: 12, background: 'none', border: 0, cursor: 'pointer' }}
              >
                Use phone number instead
              </button>
            </div>
          </>
        )}

        <p style={{
          marginTop: 18,
          fontFamily: 'var(--lp-mono)',
          fontSize: 10,
          letterSpacing: '0.10em',
          color: 'var(--lp-ink-mute)',
          textAlign: 'center',
        }}>
          By continuing you agree to our{' '}
          <a href="https://www.proxiaistudio.com/terms" target="_blank" rel="noopener noreferrer" className="lp-link">Terms</a> and{' '}
          <a href="https://www.proxiaistudio.com/privacy" target="_blank" rel="noopener noreferrer" className="lp-link">Privacy Policy</a>.
        </p>
      </div>
    </LightModal>
  );
};

export default RegistrationModal;
